import { deriveTokens, type ThemeRecipe } from './recipe';

// Curated recipes surfaced in the editor's "Paletti" tab. Each entry
// covers a coherent 2–3 colour ThemeRecipe so `deriveTokens` /
// `derivePalette` produce a legible surface out of the box.
//
// Adding a palette = one entry. Ordering is display order in the UI.

export interface NamedPalette {
  key: string;
  name: string; // Finnish label
  recipe: ThemeRecipe;
}

export const COLOR_PALETTES: readonly NamedPalette[] = [
  {
    key: 'sunset',
    name: 'Auringonlasku',
    recipe: { base: '#ff8a5c', secondary: '#ffb56b', tertiary: '#7d5aa8', mood: 'bright' },
  },
  { key: 'sea', name: 'Meri', recipe: { base: '#4fb8c9', secondary: '#0ea5e9', mood: 'bright' } },
  {
    key: 'forest',
    name: 'Metsä',
    recipe: { base: '#4d9463', secondary: '#8bbf6f', tertiary: '#c5db83', mood: 'soft' },
  },
  {
    key: 'candy',
    name: 'Karkki',
    recipe: { base: '#ff8fc0', secondary: '#ffcae0', tertiary: '#b993ff', mood: 'bright' },
  },
  { key: 'night', name: 'Yö', recipe: { base: '#1e293b', secondary: '#334155', mood: 'dark' } },
  {
    key: 'peach',
    name: 'Persikka',
    recipe: { base: '#ffcaa8', secondary: '#ffe4b8', mood: 'soft' },
  },
  {
    key: 'mint',
    name: 'Minttu',
    recipe: { base: '#7ce4c3', secondary: '#bdf5db', mood: 'bright' },
  },
  {
    key: 'lavender',
    name: 'Laventeli',
    recipe: { base: '#a794e0', secondary: '#d4c8f5', mood: 'soft' },
  },
  {
    key: 'coral',
    name: 'Koralli',
    recipe: { base: '#ff6b6b', secondary: '#ffa8a8', mood: 'bright' },
  },
  {
    key: 'lemon',
    name: 'Sitruuna',
    recipe: { base: '#fde047', secondary: '#fef08a', mood: 'bright' },
  },
  {
    key: 'cocoa',
    name: 'Kaakao',
    recipe: { base: '#6b4423', secondary: '#a67c52', mood: 'dark' },
  },
  {
    key: 'aurora',
    name: 'Revontuli',
    recipe: { base: '#5eead4', secondary: '#818cf8', tertiary: '#a78bfa', mood: 'dark' },
  },
];

export const COLOR_PALETTES_BY_KEY: Readonly<Record<string, NamedPalette>> = Object.fromEntries(
  COLOR_PALETTES.map((p) => [p.key, p]),
);

// -------- deterministic randomizer --------
//
// Curated HSL space that never picks near-grey or clashing hue triads.
// Hue is picked from the seed; saturation and lightness are clamped to
// the "pleasant pastel/bright" band the recipe.ts palette params expect.

const SAT_MIN = 0.55;
const SAT_MAX = 0.8;
const LIGHT_MIN = 0.55;
const LIGHT_MAX = 0.72;

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hslHexInBand(rand: () => number, hue: number): string {
  const s = SAT_MIN + rand() * (SAT_MAX - SAT_MIN);
  const l = LIGHT_MIN + rand() * (LIGHT_MAX - LIGHT_MIN);
  return hslToHex(hue, s, l);
}

function hslToHex(h: number, s: number, l: number): string {
  const hh = ((h % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((hh / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;
  if (hh < 60) [r, g, b] = [c, x, 0];
  else if (hh < 120) [r, g, b] = [x, c, 0];
  else if (hh < 180) [r, g, b] = [0, c, x];
  else if (hh < 240) [r, g, b] = [0, x, c];
  else if (hh < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  const to255 = (v: number) =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${to255(r)}${to255(g)}${to255(b)}`;
}

// Produce a triadic (offsets ~120°) or analogous (~30°) 3-stop recipe
// depending on the seed's low bit. Deterministic per seed.
export function randomizeRecipe(seed: number): ThemeRecipe {
  const rand = mulberry32(seed);
  const baseHue = Math.floor(rand() * 360);
  const useTriadic = (seed & 1) === 0;
  const offset = useTriadic ? 120 : 30;
  const base = hslHexInBand(rand, baseHue);
  const secondary = hslHexInBand(rand, baseHue + offset);
  const tertiary = hslHexInBand(rand, baseHue + offset * 2);
  return { base, secondary, tertiary, mood: 'bright' };
}

// Sanity check helper — recipe produces valid tokens. Used by tests.
export function isRecipeUsable(recipe: ThemeRecipe): boolean {
  try {
    const tokens = deriveTokens(recipe);
    return typeof tokens.bodyBackground === 'string' && tokens.bodyBackground.length > 0;
  } catch {
    return false;
  }
}
